import { default_plus_img } from '@services/MarppleShop/features/Creator/constant';
import { PostMessageNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { htmlIf } from '@services/MarppleShop/shared/util';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import { html, View } from 'rune-ts';
import klass from './CreatorLogo.module.scss';
import { CreatorProfile } from './CreatorProfile.ts';

export interface CreatorLogoProps {
  plus?: {
    logo_url: string | null;
    name: string;
    link?: string;
    navigate?: PostMessageNavigate;
  };
  creator: {
    profile_url?: string | null;
    logo_url?: string | null;
    name: string | null;
    link?: string;
    navigate?: PostMessageNavigate;
  };
  is_mobile: boolean;
  theme?: 'dark' | 'bright';
  type?: 'name' | 'logo';
}

export class CreatorLogo extends View<CreatorLogoProps> {
  static isSurrounded(props: { plus: boolean; creator: boolean }) {
    return props.plus && props.creator;
  }

  static isEmpty(props: { plus: boolean; creator: boolean }) {
    return !props.plus && !props.creator;
  }

  changeTheme(theme: CreatorLogoProps['theme'], instant: boolean) {
    const element = this.element();
    const logo_container_element = element.querySelector(`.${klass.logo_container}`) as HTMLElement;
    this.data.theme = theme;

    if (theme === 'bright') {
      logo_container_element.classList.remove(klass.dark);
    } else {
      logo_container_element.classList.add(klass.dark);
    }

    if (instant) {
      element.classList.add(klass.instant_transition);
      element.offsetHeight; // force reflow
      element.classList.remove(klass.instant_transition);
    }

    this.redraw();
  }

  override template() {
    const { plus, creator, is_mobile } = this.data;
    const theme_class = this.data.theme == 'dark' ? klass.dark : '';

    if (CreatorLogo.isEmpty({ plus: !!plus, creator: !!creator })) return html`<span></span>`;

    const surrounded = CreatorLogo.isSurrounded({ plus: !is_mobile && !!plus, creator: !!creator });
    const without_bg = creator && (!plus || is_mobile);

    if (is_mobile) {
      if (this.data.theme == 'dark') {
        return html` <div class="${klass.plus_creator_logo}">
          <div
            class="${klass.logo_container} ${htmlIf(klass.surrounded, surrounded)} ${theme_class} ${without_bg
              ? klass.no_bg
              : ''}"
          ></div>
        </div>`;
      } else {
        return html` <div class="${klass.plus_creator_logo}">
          <div
            class="${klass.logo_container} ${htmlIf(klass.surrounded, surrounded)} ${theme_class} ${without_bg
              ? klass.no_bg
              : ''}"
          >
            ${this.#creatorProfileTemplate()}
          </div>
          ${this.#creatorNameTemplate()}
        </div>`;
      }
    }

    if (this.data.theme == 'dark') {
      return html` <div class="${klass.plus_creator_logo}">
        <div
          class="${klass.logo_container} ${htmlIf(klass.surrounded, surrounded)} ${theme_class} ${without_bg
            ? klass.no_bg
            : ''}"
        >
          ${this.#plusLogoTemplate()}
          ${surrounded
            ? this.#creatorProfileTemplate()
            : creator.logo_url
              ? this.#creatorLogoTemplate()
              : this.#creatorNameTemplate()}
        </div>
      </div>`;
    }

    return html`
      <div class="${klass.plus_creator_logo}">
        <div
          class="${klass.logo_container} ${htmlIf(klass.surrounded, surrounded)} ${theme_class} ${without_bg
            ? klass.no_bg
            : ''}"
        >
          ${this.#plusLogoTemplate()} ${this.#creatorProfileTemplate()}
        </div>
        ${this.#creatorNameTemplate()}
      </div>
    `;
  }

  #plusLogoTemplate() {
    const { plus } = this.data;
    return plus
      ? html`<${plus.link ? 'a' : 'div'}
                class="${klass.logo_round_box}"
                ${plus.link ? html`href="${plus.link}"` : ''}
                ${plus.navigate ? html`data-post-message="${dataStr(plus.navigate)}"` : ''}
                ><img class="${klass.plus_logo_img}" src="${
                  plus.logo_url ?? default_plus_img
                }" alt="creator group logo"
              /></${plus.link ? 'a' : 'div'}>`
      : '';
  }

  #creatorNameTemplate() {
    const { creator } = this.data;
    return html`<a
      ${creator.link ? html`href="${creator.link}"` : ''}
      ${creator.navigate ? html`data-post-message="${dataStr(creator.navigate)}"` : ''}
      class="${klass.creator_name}"
    >
      <div class="${klass.name} ${font('20_bold', '14_bold')}">${html.preventEscape(creator.name ?? '')}</div>
    </a>`;
  }

  #creatorLogoTemplate() {
    const { creator } = this.data;
    return html`<a
      ${creator.link ? html`href="${creator.link}"` : ''}
      ${creator.navigate ? html`data-post-message="${dataStr(creator.navigate)}"` : ''}
      class="${klass.logo_rect_box}"
      ><img class="${klass.logo_img}" src="${creator.logo_url}" alt="creator logo"
    /></a>`;
  }

  #creatorProfileTemplate() {
    const { creator } = this.data;
    return html` <a
      ${creator.link ? html`href="${creator.link}"` : ''}
      ${creator.navigate ? html`data-post-message="${dataStr(creator.navigate)}"` : ''}
      class="${klass.creator_name}"
    >
      ${new CreatorProfile({ profile_url: creator.profile_url, size: 'large', no_border: true })}
    </a>`;
  }
}
